import {
  Box,
  Collapse,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import {
  setSelectedFieldDrawer,
  deleteFieldOfSelectedFile,
  selectField,
  setSelectedField,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
  setReduxSelectedFile,
  updateFileInSignRequest,
  setSideCollpased,
  setMobilePropertiesRequested,
  setClipboardField,
} from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { RequestParticipant } from "models/interfaces";
import { getNextAvailableId } from "../SignRequestHelper";
import { isTruthy } from "helpers/methods";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useLocation } from "react-router-dom";
import viewpaths from "global/constants/ViewPathConstants";

export type ContextMenuProps = {
  anchorEl: null | HTMLElement;
  isOpen: boolean;
  setAnchorEl: (value: null | HTMLElement) => void;
  field: any;
  isBulkSend: boolean;
  isMobile?: boolean;
  onEditSignature?: () => void;
};

const Shortcut = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      ml: 4,
      color: "text.secondary",
      letterSpacing: 0.2,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typography>
);

const ContextMenu = ({
  setAnchorEl,
  field,
  isBulkSend,
  isMobile,
  onEditSignature,
}: ContextMenuProps) => {
  const { signRequest, selectedField, selectedFile } = useAppSelector(
    (state) => state.app,
  );
  const dispatch = useAppDispatch();
  const [showSignerList, setShowSignerList] = useState(false);
  const theme = useTheme();
  const location = useLocation();

  const liveField =
    selectedFile?.fields?.find((f: any) => f.id === field.id) || field;

  const isSelfSign = location.pathname === viewpaths.createSelfSignRequestPath;

  const isSelfSignImageWithValue =
    isSelfSign &&
    (field.type === "signature" || field.type === "initials") &&
    isTruthy(field.value);

  const isSignatureField = field.type === "signature";
  const isInitialField = field.type === "initials";

  const editLabel = isSignatureField
    ? "Edit Signature"
    : isInitialField
      ? "Edit Initial"
      : "";

  const removeLabel = isSignatureField
    ? "Remove Signature"
    : isInitialField
      ? "Remove Initial"
      : "";

  const assignedSigner = signRequest.signers.find(
    (s: RequestParticipant) => s.id === liveField.signerIndex,
  );
  const close = () => setAnchorEl(null);

  const handleClearValue = () => {
    dispatch(updateFieldOfSelectedFile({ id: field.id, value: "" }));
    dispatch(updateFieldInSignRequest({ id: field.id, value: "" }));
    close();
  };

  const reAssignSigner = (signer: RequestParticipant) => {
    const propsToUpdateField = {
      id: field.id,
      signerIndex: signer.id,
      color: signer.color,
    };
    dispatch(updateFieldOfSelectedFile(propsToUpdateField));
    dispatch(updateFieldInSignRequest(propsToUpdateField));
    if (selectedField.id === field.id) {
      dispatch(setSelectedField(propsToUpdateField));
    }
    setShowSignerList(false);
  };

  const handleProperties = () => {
    dispatch(selectField(field));
    dispatch(setSideCollpased(false));
    if (isMobile) {
      dispatch(setMobilePropertiesRequested(true));
    }
    dispatch(setSelectedFieldDrawer(false));
    setTimeout(() => {
      dispatch(setSelectedFieldDrawer(true));
    }, 0);
    close();
  };

  const handleDuplicate = () => {
    const newField = structuredClone(field);
    let px = newField.fieldPageX + 3;
    let py = newField.fieldPageY + 3;
    if (py + newField.fieldPageHeight >= 98) py -= 6;
    if (px + newField.fieldPageWidth >= 98) px -= 6;
    newField.fieldPageX = px;
    newField.fieldPageY = py;
    newField.id = getNextAvailableId(selectedFile);
    if (newField.type === "radio_button") {
      newField.value = "false";
      const siblingCount = selectedFile.fields.filter(
        (f: any) => f.type === "radio_button" && f.groupId === newField.groupId,
      ).length;
      newField.label = `Option ${siblingCount + 1}`;
    }
    const updatedFile = {
      ...selectedFile,
      fields: [...selectedFile.fields, newField],
    };
    dispatch(setReduxSelectedFile(updatedFile));
    dispatch(updateFileInSignRequest(updatedFile));
    close();
  };

  const handleCopy = () => {
    dispatch(setClipboardField(field));
    close();
  };

  const handleCut = () => {
    dispatch(setClipboardField(field));
    dispatch(deleteFieldOfSelectedFile(field));
    close();
  };

  const handleDelete = () => {
    dispatch(deleteFieldOfSelectedFile(field));
    close();
  };

  const eligibleSigners = signRequest.signers.filter(
    (s: RequestParticipant) => !s.cc,
  );

  return (
    <MenuList dense disablePadding sx={{ py: 0.5, minWidth: 240 }}>
      {isSelfSignImageWithValue && (
        <MenuItem
          onClick={() => {
            onEditSignature?.();
            close();
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{editLabel}</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={handleProperties}>
        <ListItemIcon>
          <TuneRoundedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Properties</ListItemText>
      </MenuItem>

      <MenuItem onClick={handleDuplicate}>
        <ListItemIcon>
          <FileCopyOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Duplicate</ListItemText>
      </MenuItem>

      <MenuItem onClick={handleCut}>
        <ListItemIcon>
          <ContentCutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Cut</ListItemText>
        <Shortcut>Ctrl+X</Shortcut>
      </MenuItem>

      <MenuItem onClick={handleCopy}>
        <ListItemIcon>
          <ContentCopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Copy</ListItemText>
        <Shortcut>Ctrl+C</Shortcut>
      </MenuItem>

      {!isBulkSend && eligibleSigners.length > 0 && !isSelfSign && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => setShowSignerList((v) => !v)}
            sx={{ alignItems: "center" }}
            aria-haspopup="true"
            aria-expanded={showSignerList}
            aria-controls={
              showSignerList ? "change-signer-submenu" : undefined
            }
          >
            <ListItemIcon>
              <PersonOutlineRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Change signer"
              secondary={assignedSigner?.name}
              secondaryTypographyProps={{
                sx: {
                  fontSize: 12,
                  color: "text.secondary",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: 140,
                },
              }}
            />
            {assignedSigner?.color && (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: assignedSigner.color,
                  ml: 1,
                  mr: 0.5,
                  flexShrink: 0,
                }}
              />
            )}
            {showSignerList ? (
              <KeyboardArrowUpIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
            ) : (
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
            )}
          </MenuItem>
          <Collapse in={showSignerList} unmountOnExit>
            <Box id="change-signer-submenu" sx={{ pl: 2, pr: 1, py: 0.25 }}>
              {eligibleSigners.map((signer: RequestParticipant) => {
                const isCurrent = signer.id === liveField.signerIndex;
                return (
                  <MenuItem
                    key={signer.id}
                    onClick={() => reAssignSigner(signer)}
                    selected={isCurrent}
                    sx={{ borderRadius: 1, my: 0.25 }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: signer.color,
                        mr: 1.25,
                        flexShrink: 0,
                      }}
                    />
                    <ListItemText
                      primary={signer.name || signer.email}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: 13,
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Box>
          </Collapse>
        </>
      )}

      <Divider sx={{ my: 0.5 }} />
      {isSelfSignImageWithValue && (
        <MenuItem
          sx={{
            color: theme.palette.error.main,
            "& .MuiListItemIcon-root": { color: theme.palette.error.main },
          }}
          onClick={handleClearValue}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{removeLabel}</ListItemText>
        </MenuItem>
      )}

      <MenuItem
        onClick={handleDelete}
        sx={{
          color: theme.palette.error.main,
          "& .MuiListItemIcon-root": { color: theme.palette.error.main },
        }}
      >
        <ListItemIcon>
          <DeleteOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
        <Shortcut>Delete</Shortcut>
      </MenuItem>
    </MenuList>
  );
};

export default ContextMenu;
